import { useState, useCallback } from 'react';
import { reviewRequestApi } from '../api/reviewRequest';
import type { ReviewRequestCampaign, GenerateReviewRequestPayload } from '../api/reviewRequest';

export function useReviewRequest() {
  const [campaigns, setCampaigns] = useState<ReviewRequestCampaign[]>([]);
  const [activeCampaign, setActiveCampaign] = useState<ReviewRequestCampaign | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchCampaigns = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reviewRequestApi.list(p);
      setCampaigns(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load review request campaigns.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GenerateReviewRequestPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await reviewRequestApi.generate(payload);
      const campaign = res.data.data;
      setActiveCampaign(campaign);
      setCampaigns((prev) => [campaign, ...prev]);
      return campaign;
    } catch (err: any) {
      const msg = err?.message ?? 'Review request generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteCampaign = useCallback(async (id: number) => {
    try {
      await reviewRequestApi.destroy(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      if (activeCampaign?.id === id) setActiveCampaign(null);
    } catch {
      setError('Failed to delete campaign.');
    }
  }, [activeCampaign]);

  return {
    campaigns, activeCampaign, loading, generating, error, total, page, lastPage,
    fetchCampaigns, generate, deleteCampaign, setActiveCampaign, setError,
  };
}
